export const ERROR_MESSAGES: Readonly<Record<string, string>> = {
    unauthorized_request: "You don't have permission to perform this action. Contact an administrator if you need access.",

    // Shopify trial extension (POST /v2/companies/{companyId}/shopify/trials/extend).
    // Codes not listed here fall through verbatim, which is what we want for the
    // 502 "extend trial failed: <status>: <body>" — it carries the real reason.
    invalid_trial_extension_days: 'Days must be a whole number of at least 1.',
    invalid_trial_extension_reason: 'The reason must be between 5 and 500 characters.',
    invalid_company_id: 'That company id is not valid.',
    neatolabs_company_required: 'Trial extensions can only be run from the Neatolabs company.',
    admin_role_required: 'You need the Admin role to extend a trial.',
    shopify_shop_not_found: 'No Shopify store matches that name.',
    trial_extension_not_configured: 'Trial extensions are not configured in this environment.',
}

const extractErrorMessage = (error: unknown, visited = new Set<object>()): string | null => {
    if (typeof error === 'string') {
        return error
    }

    if (typeof error === 'number' || typeof error === 'boolean') {
        return String(error)
    }

    if (!error || typeof error !== 'object' || visited.has(error)) {
        return null
    }

    visited.add(error)

    const errorRecord = error as Record<string, unknown>
    const response = errorRecord.response
    const responseData = (
        response &&
        typeof response === 'object' &&
        'data' in response
    )
        ? (response as Record<string, unknown>).data
        : null

    const candidates = [
        responseData,
        errorRecord.error,
        errorRecord.code,
        errorRecord.message,
    ]

    for (const candidate of candidates) {
        const message = extractErrorMessage(candidate, visited)
        if (message) return message
    }

    return null
}

export const getErrorMessage = (
    error: unknown,
    fallback = 'An unknown error occurred.',
): string => {
    const errorMessage = extractErrorMessage(error)

    if (!errorMessage) return fallback

    return ERROR_MESSAGES[errorMessage.trim()] ?? errorMessage
}
